import { FC, useEffect, useState } from 'react'
import { useAccess } from '../../hooks/useAccess'
import { useHandleField } from '../../hooks/useHandleField'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import { getCurrentUser } from '../../store/authSlice'
import { ITicketPriority } from '../../model/TTicketPriority'
import { ITicketStatus } from '../../model/TTicketStatus'
import { isEqualPlaneObjs } from '../../utils/isEqualObjs'
import Divider from '../common/Divider'
import Button from '../form/Button'
import Input from '../form/Input'
import UserMultiSearchField from '../user/UserMultiSearchField'
import UserRolesSelectSearch from '../user/UserRoleMultiSearchField'
import TicketPrioritySelect from './TicketPrioritySelect'
import TicketStatusSelect from './TicketStatusSelect'
import RelatedTickets from './RelatedTickets'
import { ITicket } from '../../model/ITicket'
import TextButton from '../common/buttom/TextButton'
import Checkbox from '../form/Checkbox'

export interface ITicketParamsState {
  assignedAgentRoleId?: number | undefined
  assignedVendorRoleId?: number | undefined
  assignedUserId?: string
  assignedVendorId?: string
  followers: string
  extraFollowers: string
  enableFollowersNotifications: boolean
  priorityId: ITicketPriority['id']
  statusId: ITicketStatus['id']
  orderId?: ITicket['orderId']
}

const TicketParamsForm: FC<{
  state: ITicketParamsState
  stateOriginal?: ITicketParamsState
  stateErr?: Partial<Record<keyof ITicketParamsState, string>>
  setState: React.Dispatch<React.SetStateAction<ITicketParamsState & any>>
  loading?: boolean
  handleUpdateTicket?: () => unknown
  currentTicket?: ITicket
}> = ({ state, stateOriginal, stateErr, setState, loading, handleUpdateTicket, currentTicket }) => {
  const handleField = useHandleField(setState)
  const [isNoOriginal, setIsNoOriginal] = useState<boolean>(false)
  const { getSettingAccess } = useAccess()
  const currentUser = useTypedSelector(getCurrentUser())

  const handleAssignYourself = () => {
    if (!currentUser?.id) return
    const followerIds = state.followers ? state.followers.split(',').filter(Boolean) : []
    if (followerIds.includes(currentUser.id)) return
    const newFollowers = [...followerIds, currentUser.id].join(',')
    handleField('followers')({ target: { value: newFollowers } } as React.ChangeEvent<HTMLInputElement>)
  }

  // --
  // -- Check if original state has changed
  // --
  useEffect(() => {
    setIsNoOriginal(() => !!stateOriginal && !isEqualPlaneObjs(state, stateOriginal))
  }, [state])

  const handleReset = () => stateOriginal && setState(() => stateOriginal)

  return (
    <div className='flex flex-col gap-4'>
      {!!getSettingAccess('ticket-asigned-agent') && (
        <UserRolesSelectSearch
          label='Assigned Agent Role'
          name='assignedAgentRoleId'
          placeholder='Choose Agent Role'
          type='text'
          value={state.assignedAgentRoleId}
          handle={handleField('assignedAgentRoleId')}
          disabled={loading}
          error={stateErr?.assignedAgentRoleId}
          roles={['ROLE_AGENT']}
        />
      )}
      {!!getSettingAccess('ticket-asigned-vendor') && (
        <UserRolesSelectSearch
          label='Assigned Vendor Role'
          name='assignedVendorId'
          placeholder='Choose Vendor Role'
          type='text'
          value={state.assignedVendorRoleId}
          handle={handleField('assignedVendorRoleId')}
          disabled={loading}
          error={stateErr?.assignedVendorRoleId}
          roles={['ROLE_VENDOR']}
        />
      )}
      {!!getSettingAccess('ticket-followers') && (
        <>
          <UserMultiSearchField
            label='Assigned Agents'
            placeholder='Select agents to assign'
            name='followers'
            value={state.followers}
            handle={handleField('followers')}
            disabled={loading}
            error={stateErr?.followers}
            width='100px'
          />
          <TextButton onClick={handleAssignYourself}>Assign yourself</TextButton>
          <div
            tabIndex={-1}
            role='button'
            onClick={() => { handleField('enableFollowersNotifications')(!state.enableFollowersNotifications) }}
            className='flex justify-between items-center'
          >
            Enable email notifications
            <Checkbox name={'enableNotifications'} value={state.enableFollowersNotifications} enablePropagation />
          </div>
        </>
      )
      }
      {
        !!getSettingAccess('ticket-external-followers') && (
          <Input
            label='External Followers'
            name='extraFollowers'
            type='text'
            size='small'
            value={state.extraFollowers}
            handle={handleField('extraFollowers')}
            disabled={loading}
            error={stateErr?.extraFollowers}
          />
        )
      }
      {
        (!!getSettingAccess('ticket-form-status-edit') ||
          !!getSettingAccess('ticket-form-priority-edit')) && (
          <>
            <Divider />
            {!!getSettingAccess('ticket-form-priority-edit') && (
              <TicketPrioritySelect
                label='Priority'
                name='priorityId'
                value={state.priorityId}
                handle={handleField('priorityId')}
                disabled={loading}
                error={stateErr?.priorityId}
              />
            )}
            {!!getSettingAccess('ticket-form-status-edit') && (
              <TicketStatusSelect
                label='Status'
                name='statusId'
                value={state.statusId}
                handle={handleField('statusId')}
                disabled={loading}
                error={stateErr?.statusId}
              />
            )}
          </>
        )
      }

      {
        !!isNoOriginal && (
          <div className='flex mt-4 gap-2'>
            <Button variant='outline' size='small' onClick={handleReset}>
              Reset
            </Button>
            <Button size='small' onClick={() => handleUpdateTicket && handleUpdateTicket()}>
              Update
            </Button>
          </div>
        )
      }
      {
        !!currentTicket && (
          <RelatedTickets orderId={currentTicket?.orderId} ticketId={currentTicket.id} />
        )
      }
    </div >
  )
}

export default TicketParamsForm
